import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import type { SupportCustomClaims } from "@srd/shared";
import { auth, googleProvider, startTotpMfaEnrollmentPlaceholder } from "../firebase";

interface AuthContextValue {
  user: User | null;
  claims: SupportCustomClaims | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  startMfaPlaceholder: () => Promise<string>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function readSupportClaims(user: User): Promise<SupportCustomClaims | null> {
  const token = await user.getIdTokenResult(true);
  const role = token.claims.role;
  const tenantId = token.claims.tenantId;
  if (
    (role !== "technician" && role !== "admin" && role !== "auditor") ||
    typeof tenantId !== "string" ||
    !tenantId
  ) {
    return null;
  }
  return {
    role,
    tenantId,
    canUnattended: Boolean(token.claims.canUnattended),
    deviceGroups: Array.isArray(token.claims.deviceGroups)
      ? (token.claims.deviceGroups as unknown[]).filter((g): g is string => typeof g === "string")
      : undefined,
    mfaRequired:
      token.claims.mfaRequired === undefined ? true : Boolean(token.claims.mfaRequired),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<SupportCustomClaims | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (next) => {
      void (async () => {
        setUser(next);
        if (!next) {
          setClaims(null);
          setLoading(false);
          return;
        }
        try {
          setClaims(await readSupportClaims(next));
        } catch {
          setClaims(null);
        } finally {
          setLoading(false);
        }
      })();
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      claims,
      loading,
      signInGoogle: async () => {
        await signInWithPopup(auth, googleProvider);
      },
      signInEmail: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOutUser: async () => {
        await signOut(auth);
      },
      startMfaPlaceholder: startTotpMfaEnrollmentPlaceholder,
      refreshClaims: async () => {
        if (!auth.currentUser) return;
        setClaims(await readSupportClaims(auth.currentUser));
      },
    }),
    [user, claims, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
